import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Select, 
  Space, 
  Tag, 
  Button, 
  message,
  Typography,
  Avatar
} from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import PageLayout from '../../components/layout/PageLayout';
import userService from '../../services/userService';
import { useUser } from '../../context/UserContext';
import moment from 'moment';

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const UserList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [userList, setUserList] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`
  });
  
  // Filters state
  const [filters, setFilters] = useState({
    searchText: '',
    sub_status: null
  });

  // Current timestamp for subscription comparison
  const now_timestamp = moment().unix();

  // Table columns configuration
  const userColumns = [
    {
      title: 'Name',
      key: 'name',
      render: (text, record) => (
        <span>{`${record.user_firstname || ''} ${record.user_lastname || ''}`.trim()}</span>
      ),
      sorter: (a, b) => {
        const nameA = `${a.user_firstname || ''} ${a.user_lastname || ''}`.trim();
        const nameB = `${b.user_firstname || ''} ${b.user_lastname || ''}`.trim();
        return nameA.localeCompare(nameB);
      }
    },
    {
      title: 'Mobile Number',
      dataIndex: 'user_mobile',
      key: 'user_mobile',
      sorter: (a, b) => a.user_mobile?.localeCompare(b.user_mobile)
    },
    {
      title: 'Mobile Verified',
      dataIndex: 'user_mobile_verified',
      key: 'user_mobile_verified',
      defaultSortOrder: 'ascend',
      render: (text) => (
        <Tag color={text === 'Y' ? 'green' : 'red'}>
          {text === 'Y' ? 'Yes' : 'No'}
        </Tag>
      ),
      filters: [
        { text: 'Yes', value: 'Y' },
        { text: 'No', value: 'N' }
      ],
      onFilter: (value, record) => record.user_mobile_verified === value
    },
    {
      title: 'Is Subscribed',
      key: 'is_subscribed',
      defaultSortOrder: 'ascend',
      render: (text, record) => {
        const isSubscribed = record.subscription_end_date_timestamp > now_timestamp;
        return (
          <Tag color={isSubscribed ? 'green' : 'red'}>
            {isSubscribed ? 'Yes' : 'No'}
          </Tag>
        );
      },
      filters: [
        { text: 'Yes', value: 'subscribed' },
        { text: 'No', value: 'not_subscribed' }
      ],
      onFilter: (value, record) => {
        const isSubscribed = record.subscription_end_date_timestamp > now_timestamp;
        if (value === 'subscribed') return isSubscribed;
        if (value === 'not_subscribed') return !isSubscribed;
        return true;
      }
    },
    {
      title: 'Subscription End Date',
      dataIndex: 'subscription_end_date',
      key: 'subscription_end_date',
      render: (text) => text ? moment(text).format('YYYY-MM-DD') : '-',
      sorter: (a, b) => moment(a.subscription_end_date).unix() - moment(b.subscription_end_date).unix()
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => text ? moment(text).format('YYYY-MM-DD HH:mm') : '-',
      sorter: (a, b) => moment(a.created_at).unix() - moment(b.created_at).unix()
    },
    {
      title: 'Action',
      key: 'action',
      fixed: 'right',
      width: 100,
      render: (text, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/users/details/${record.user_id}`)}
            size="small"
          >
            Details
          </Button>
        </Space>
      )
    }
  ];

  // Fetch users data
  const getUsers = async (params = {}) => {
    try {
      setLoading(true);
      const queryParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        ...params
      };
      
      console.log('Fetching users with params:', queryParams);
      const response = await userService.getUsers(queryParams);
      console.log('API Response:', response);
      
      const { data } = response;
      
      if (data && data.status) {
        setUserList(data.data.data || []);
        setPagination(prev => ({
          ...prev,
          ...data.data.pagination,
          current: data.data.pagination.currentPage
        }));
      } else {
        console.log('API returned no data or status false:', data);
        setUserList([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      message.error('Failed to fetch users');
      setUserList([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, searchText: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getUsers({
      search: value,
      sub_status: filters.sub_status,
      perPage: pagination.pageSize,
      currentPage: 1
    });
  };

  // Handle subscription status filter change
  const handleSubStatusChange = (value) => {
    setFilters(prev => ({ ...prev, sub_status: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getUsers({
      search: filters.searchText,
      sub_status: value,
      perPage: pagination.pageSize,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting)
  const handleTableChange = (newPagination, tableFilters, sorter) => {
    const { current, pageSize } = newPagination;
    
    if (current !== pagination.current || pageSize !== pagination.pageSize) {
      setPagination(prev => ({
        ...prev,
        current,
        pageSize
      }));
      
      getUsers({
        search: filters.searchText,
        sub_status: filters.sub_status,
        perPage: pageSize,
        currentPage: current
      });
    }
  };

  // Load data on component mount
  useEffect(() => {
    getUsers();
  }, []);

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">User Management</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="User Management"
      desc="Manage users and their subscriptions"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        {/* Table Operations */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '16px',
          flexWrap: 'wrap',
          gap: '8px'
        }}>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text strong>Subscription Status:</Text>
              <Select
                placeholder="Subscription Status"
                style={{ width: 200 }}
                value={filters.sub_status}
                onChange={handleSubStatusChange}
                allowClear
              >
                <Option value={null}>All</Option>
                <Option value="active">Active</Option>
                <Option value="expired">Expired</Option>
                <Option value="never_subscribed">Never Subscribed</Option>
              </Select>
            </span>
          </div>
          
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Search
              placeholder="Search users..."
              style={{ width: 300 }}
              value={filters.searchText}
              onChange={(e) => setFilters(prev => ({ ...prev, searchText: e.target.value }))}
              onSearch={handleSearch}
              allowClear
            />
          </div>
        </div>

        {/* Data Table */}
        <Table
          columns={userColumns}
          dataSource={userList}
          rowKey="user_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
        />
      </Card>
    </PageLayout>
  );
};

export default UserList;